import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { CASE_STUDIES, EXTRA_CASES } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";

export const CaseStudies = () => {
  const [open, setOpen] = useState(null);
  return (
  <section data-testid="casestudies-section" id="work" className="relative py-28 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="max-w-3xl mb-20">
        <Reveal>
          <Eyebrow testid="casestudies-eyebrow">Case Studies</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.05]">
            The work, in <span className="italic text-gold-gradient">full detail.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-cream/55 mt-6 text-base sm:text-lg leading-relaxed">
            Real briefs, real turnarounds — how the biggest wins actually happened, from
            challenge to approach to results.
          </p>
        </Reveal>
      </div>

      <div className="space-y-6">
        {CASE_STUDIES.map((cs, i) => (
          <motion.article
            key={cs.n}
            data-testid={`case-study-${i}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="group relative border border-white/10 rounded-3xl p-8 lg:p-12 bg-charcoal-800/40 hover:border-gold/30 transition-colors duration-500 grid lg:grid-cols-12 gap-8 lg:gap-12"
          >
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4 mb-5">
                <span className="font-serif text-5xl text-gold/30 leading-none">{cs.n}</span>
                <span className="text-[11px] uppercase tracking-[0.25em] text-gold">{cs.category}</span>
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl text-cream leading-tight">{cs.client}</h3>
              <p className="text-gold-gradient font-serif italic text-2xl mt-2">{cs.headline}</p>
              <div className="mt-6 space-y-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-cream/40 mb-1.5">Challenge</p>
                  <p className="text-cream/60 text-sm leading-relaxed">{cs.challenge}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-cream/40 mb-1.5">Approach</p>
                  <p className="text-cream/60 text-sm leading-relaxed">{cs.approach}</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:border-l lg:border-white/10 lg:pl-12">
              <div className="flex items-center gap-2 mb-6 text-cream/40">
                <span className="text-[11px] uppercase tracking-[0.25em]">Results</span>
                <ArrowUpRight size={14} className="text-gold" />
              </div>
              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
                {cs.metrics.map((m, j) => (
                  <div key={j} className="bg-charcoal-900 p-6 lg:p-8">
                    <p className="font-serif text-4xl lg:text-5xl text-gold-gradient leading-none tracking-tight">{m.v}</p>
                    <p className="text-cream/50 text-xs sm:text-sm mt-3 leading-snug">{m.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      {/* Expandable additional studies */}
      <div className="mt-6 border border-white/10 rounded-3xl overflow-hidden divide-y divide-white/10">
        <Reveal>
          <button
            className="w-full text-left px-8 lg:px-12 pt-8 pb-6 cursor-default"
            tabIndex={-1}
          >
            <span className="text-[11px] uppercase tracking-[0.3em] text-gold">More Work</span>
            <p className="font-serif text-2xl sm:text-3xl text-cream mt-2">Further selected engagements</p>
          </button>
        </Reveal>
        {EXTRA_CASES.map((cs, i) => {
          const isOpen = open === i;
          return (
            <div key={cs.client} data-testid={`extra-case-${i}`} className="group">
              <button
                data-testid={`extra-case-toggle-${i}`}
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-6 px-8 lg:px-12 py-7 hover:bg-charcoal-800/40 transition-colors"
              >
                <div className="flex items-baseline gap-4 sm:gap-6 min-w-0">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-gold/70 whitespace-nowrap hidden sm:block">{cs.category}</span>
                  <span className="font-serif text-2xl sm:text-3xl text-cream truncate">{cs.client}</span>
                  <span className="font-serif italic text-gold-gradient text-lg sm:text-xl truncate hidden md:inline">— {cs.headline}</span>
                </div>
                <motion.span
                  animate={{ rotate: isOpen ? 45 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="h-10 w-10 shrink-0 rounded-full border border-white/15 flex items-center justify-center text-gold group-hover:border-gold/50"
                >
                  <Plus size={18} />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 lg:px-12 pb-10 grid lg:grid-cols-12 gap-8 lg:gap-12">
                      <div className="lg:col-span-5 space-y-4">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.25em] text-cream/40 mb-1.5">Challenge</p>
                          <p className="text-cream/60 text-sm leading-relaxed">{cs.challenge}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.25em] text-cream/40 mb-1.5">Approach</p>
                          <p className="text-cream/60 text-sm leading-relaxed">{cs.approach}</p>
                        </div>
                      </div>
                      <div className="lg:col-span-7">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
                          {cs.metrics.map((m, j) => (
                            <div key={j} className="bg-charcoal-900 p-5 lg:p-6">
                              <p className="font-serif text-3xl lg:text-4xl text-gold-gradient leading-none tracking-tight">{m.v}</p>
                              <p className="text-cream/50 text-xs mt-2.5 leading-snug">{m.l}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};
